import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download, Share, MoreVertical } from 'lucide-react';

// Shows an "Install App" affordance everywhere. Uses the native prompt when the
// browser fired beforeinstallprompt; otherwise shows manual Add-to-Home-Screen steps
// (iOS Safari + desktops never fire the event). Hidden when already installed.
export function InstallAppButton({ variant = 'outline', className = '', testid = 'install-app-button' }) {
  const isStandalone = typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true);
  const [prompt, setPrompt] = useState(typeof window !== 'undefined' ? window.__deferredInstallPrompt : null);
  const [installed, setInstalled] = useState(isStandalone);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const onAvail = () => setPrompt(window.__deferredInstallPrompt);
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener('pwa-installable', onAvail);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('pwa-installable', onAvail);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    const p = window.__deferredInstallPrompt || prompt;
    if (p) {
      p.prompt();
      const res = await p.userChoice.catch(() => ({}));
      if (res?.outcome === 'accepted') { setInstalled(true); }
      window.__deferredInstallPrompt = null;
      setPrompt(null);
    } else {
      setShowHelp(true);
    }
  };

  const isIOS = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <>
      <Button variant={variant} size="sm" onClick={onClick} data-testid={testid} className={`gap-1.5 ${className}`}>
        <Download className="h-3.5 w-3.5" /> Install App
      </Button>
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Install Eagle Eyes app</DialogTitle>
            <DialogDescription>Add the app to your home screen — same login, same data, opens full-screen like a native app.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {isIOS ? (
              <div className="rounded-lg border border-border p-3">
                <div className="font-medium mb-1">On iPhone / iPad (Safari)</div>
                <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                  <li>Tap the <Share className="inline h-3.5 w-3.5" /> <strong>Share</strong> button.</li>
                  <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
                  <li>Tap <strong>Add</strong> — the app icon appears on your home screen.</li>
                </ol>
              </div>
            ) : (
              <div className="rounded-lg border border-border p-3">
                <div className="font-medium mb-1">On Android (Chrome)</div>
                <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                  <li>Tap the <MoreVertical className="inline h-3.5 w-3.5" /> menu (top-right).</li>
                  <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
                  <li>Confirm — the app is added to your home screen.</li>
                </ol>
              </div>
            )}
            <p className="text-xs text-muted-foreground">On a desktop browser, look for the <strong>Install</strong> icon in the address bar.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InstallAppButton;
